import React from 'react';
import { motion } from 'framer-motion';
export function MyceliumNetworkHero() {
  // Generate random mycelium network nodes - increased count
  const nodes = Array.from({
    length: 60
  }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 4 + 2
  }));
  // Generate connections between nearby nodes
  const connections = nodes.flatMap((node, i) => nodes.slice(i + 1).filter(other => {
    const dx = node.x - other.x;
    const dy = node.y - other.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return distance < 22;
  }).map(other => ({
    from: node,
    to: other,
    id: `${node.id}-${other.id}`
  })));
  return <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="xMidYMid slice" data-id="element-194" data-mp="2vs8eqp8qu0ul">
      <defs data-id="element-195" data-mp="30jqbt27nbhaa">
        <radialGradient id="nodeGradient" cx="50%" cy="50%" data-id="element-196" data-mp="30k86huentng3">
          <stop offset="0%" stopColor="#2BC7B7" stopOpacity="0.9" data-id="element-197" data-mp="1v1ztj95ec9is" />
          <stop offset="100%" stopColor="#4DE7D4" stopOpacity="0.5" data-id="element-198" data-mp="1v054bin3dv8w" />
        </radialGradient>
        <filter id="glow" data-id="element-199" data-mp="1qz34p0p9jh6e">
          <feGaussianBlur stdDeviation="0.8" result="coloredBlur" data-id="element-200" data-mp="24sug6emk1b4r" />
          <feMerge data-id="element-201" data-mp="24udtpxgtt82u">
            <feMergeNode in="coloredBlur" data-id="element-202" data-mp="201ujnahckv2s" />
            <feMergeNode in="SourceGraphic" data-id="element-203" data-mp="200sy2c2pgq6t" />
          </feMerge>
        </filter>
      </defs>

      {/* Background gradient */}
      <rect width="100" height="100" fill="url(#bgGradient)" data-id="element-204" data-mp="2esl9we3ajm3s" />
      <defs data-id="element-205" data-mp="29qcivn9zau4b">
        <linearGradient id="bgGradient" x1="0%" y1="0%" x2="0%" y2="100%" data-id="element-206" data-mp="29qvwwswicyd2">
          <stop offset="0%" stopColor="#0e2533" data-id="element-207" data-mp="2nq40dxzpesn2" />
          <stop offset="100%" stopColor="#000000" data-id="element-208" data-mp="2nosy62lwpsy2" />
        </linearGradient>
      </defs>

      {/* Mycelium connections */}
      {connections.map((conn, i) => <motion.line key={conn.id} x1={conn.from.x} y1={conn.from.y} x2={conn.to.x} y2={conn.to.y} stroke="#2BC7B7" strokeWidth="0.15" strokeOpacity="0.4" initial={{
      pathLength: 0,
      opacity: 0
    }} animate={{
      pathLength: 1,
      opacity: 0.4
    }} transition={{
      duration: 2,
      delay: i * 0.008,
      ease: 'easeInOut'
    }} data-id="element-209" data-mp="tdxea459urfz" data-mpi={i} />)}

      {/* Animated growth pulses along connections - increased count */}
      {connections.slice(0, 15).map((conn, i) => <motion.circle key={`pulse-${conn.id}`} r="0.4" fill="#4DE7D4" filter="url(#glow)" initial={{
      opacity: 0
    }} animate={{
      opacity: [0, 0.9, 0]
    }} transition={{
      duration: 2.5,
      delay: 1.5 + i * 0.2,
      repeat: Infinity,
      repeatDelay: 4
    }} data-id="element-210" data-mp="3cdhnjcdu9ned" data-mpi={i}>
          <animateMotion dur="2.5s" repeatCount="indefinite" begin={`${1.5 + i * 0.2}s`} data-id="element-211" data-mp="3v5iqaptqhxt2">
            <mpath href={`#path-${conn.id}`} xlinkHref={`#path-${conn.id}`} data-id="element-212" data-mp="1jf19yj3jzi05" />
          </animateMotion>
        </motion.circle>)}

      {/* Hidden paths for animation */}
      {connections.slice(0, 15).map(conn => <path key={`path-${conn.id}`} id={`path-${conn.id}`} d={`M ${conn.from.x} ${conn.from.y} L ${conn.to.x} ${conn.to.y}`} fill="none" opacity="0" data-id="element-213" data-mp="15i5v2a0mewc6" />)}

      {/* Mycelium nodes */}
      {nodes.map((node, i) => <motion.circle key={node.id} cx={node.x} cy={node.y} r={node.size / 10} fill="url(#nodeGradient)" filter="url(#glow)" initial={{
      scale: 0,
      opacity: 0
    }} animate={{
      scale: 1,
      opacity: 0.85
    }} transition={{
      duration: 0.6,
      delay: i * 0.015,
      ease: 'easeOut'
    }} data-id="element-214" data-mp="1tflafjj90ihb" data-mpi={i}>
          <animate attributeName="r" values={`${node.size / 10};${node.size / 10 + 0.08};${node.size / 10}`} dur={`${2.5 + Math.random() * 2}s`} repeatCount="indefinite" data-id="element-215" data-mp="20rzs25v60v0d" />
        </motion.circle>)}

      {/* Organic spore particles floating - increased count and visibility */}
      {Array.from({
      length: 20
    }, (_, i) => <motion.circle key={`spore-${i}`} r="0.2" fill="#78e1c4" opacity="0.5" initial={{
      cx: Math.random() * 100,
      cy: 100 + Math.random() * 20
    }} animate={{
      cy: -20,
      opacity: [0.5, 0.8, 0]
    }} transition={{
      duration: 12 + Math.random() * 8,
      delay: i * 0.4,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-216" data-mp="2ofr2f3x5axz0" />)}

      {/* Additional network growth effect - expanding circles */}
      {Array.from({
      length: 3
    }, (_, i) => <motion.circle key={`growth-${i}`} cx="50" cy="50" r="10" fill="none" stroke="#4DE7D4" strokeWidth="0.15" opacity="0.3" animate={{
      r: [10, 80],
      opacity: [0.3, 0]
    }} transition={{
      duration: 6,
      delay: i * 2,
      repeat: Infinity,
      ease: 'easeOut'
    }} data-id="element-217" data-mp="3r83b2qx2pmub" />)}
    </svg>;
}